import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash";
import SearchBar from "./SearchBar";
import ProductList, { ProductListHandle } from "./ProductList";
import CatalogedProduct from "shared-ui/src/types/ecommerce/CatalogedProduct";

import { useLocation, useNavigate } from "react-router-dom";
import { useSettledFeatureFlag } from "shared-ui/src/hooks/useSettledFeatureFlag";
import { navigateToShiCom } from "@lib/shiComBaseUrl";
import { AppendIfPrefixExists } from "../../../../../modules/sitePaths";
import {
  MERGE_LORDS_ENABLE_PRODUCT_LISTING_PAGE_FEATURE_FLAG,
  buildProductSearchPath,
  getProductListingSearchTerm,
} from "../../../pages/ecommerce/product-listing/productListingRoutes";
import useAddShoppingCartProduct from "@features/ecommerce/hooks/useAddShoppingCartProduct";
import useQueryProduct, {
  Pagination,
  SearchProductRequest,
} from "@features/unified-user-header-feature/hooks/useQueryProduct";

const DEBOUNCE_DELAY = 650;

const PAGINATION = {
  page: 0,
  pageSize: 25,
} as const;

interface ProductSearchProps {
  orgUnitId?: number;
  catalogId?: number;
  pagination?: Pagination;
  top?: number;
  searchPlaceholder?: string;
}

export default function ProductSearch({
  orgUnitId,
  catalogId,
  pagination = PAGINATION,
  top,
  searchPlaceholder = "Search products...",
}: ProductSearchProps) {
  const [showResults, setShowResults] = useState(false);
  const [request, setRequest] = useState<SearchProductRequest>({
    orgUnitId,
    catalogId,
    pagination: PAGINATION,
    query: undefined,
    top,
    isReturnPagingInfo: !(top !== undefined && top > 0),
  });
  const productListRef = useRef<ProductListHandle>(null);

  const { mutate: postProductAddShoppingCart } = useAddShoppingCartProduct();
  const navigate = useNavigate();
  const { enabled: productListingEnabled, isLoading: productListingLoading } =
    useSettledFeatureFlag(
      MERGE_LORDS_ENABLE_PRODUCT_LISTING_PAGE_FEATURE_FLAG,
      false,
    );
  const [pendingSearchPath, setPendingSearchPath] = useState<string>();
  const { pathname, search } = useLocation();
  const urlSearchTerm = getProductListingSearchTerm(pathname, search);
  const [inputValue, setInputValue] = useState(urlSearchTerm);

  useEffect(() => {
    setRequest((current) => {
      if (
        current.orgUnitId === orgUnitId &&
        current.catalogId === catalogId &&
        current.pagination?.page === pagination?.page &&
        current.pagination?.pageSize === pagination?.pageSize &&
        current.top === top
      ) {
        return current;
      }
      return {
        orgUnitId,
        catalogId,
        pagination,
        query: undefined,
        top,
        isReturnPagingInfo: !(top !== undefined && top > 0),
      };
    });
  }, [orgUnitId, catalogId, pagination, top]);

  const enabled = useMemo(
    () => !!request.orgUnitId && !!request.catalogId && !!request.query?.length,
    [request],
  );

  const { data: response, isLoading } = useQueryProduct(request);

  const products = useMemo((): CatalogedProduct[] => {
    if (!response?.products) {
      return [];
    }
    return response.products
      .filter((p) => p.productDescriptor && p.productClass)
      .map(
        (p): CatalogedProduct => ({
          id: `${p.productDescriptor!}|${p.productClass}`,
          productDescriptor: p.productDescriptor,
          productClass: p.productClass,
          name: p.productName,
          description: p.description,
          manufacturerPartNumber: p.manufacturerPartNumber,
          shiPartNumber: p.productDescriptor,
          price: p.price,
          imageUrl: p.imageUrl,
          availability: p.inventoryStatus,
          canAddToCart: p.canAddToCart,
        }),
      );
  }, [response?.products]);

  useEffect(() => {
    setShowResults(!!products.length);
  }, [products]);

  const updateSearchQuery = useCallback((value?: string) => {
    const newQuery = value?.trim() || undefined;
    setRequest((current) => {
      if (current.query === newQuery) {
        return current;
      }
      return {
        ...current,
        query: newQuery,
      };
    });
  }, []);

  const searchQueryDebounced = useMemo(() => {
    return debounce(updateSearchQuery, DEBOUNCE_DELAY);
  }, [updateSearchQuery]);

  useEffect(() => {
    return () => {
      searchQueryDebounced.cancel();
    };
  }, [searchQueryDebounced]);

  // Follow the PLP URL's search term (chip removal, back/forward, category links) without
  // touching what the user types; the typeahead is reset so it doesn't pop open on navigation.
  const syncedSearchTermRef = useRef(urlSearchTerm);
  useEffect(() => {
    if (syncedSearchTermRef.current === urlSearchTerm) return;
    syncedSearchTermRef.current = urlSearchTerm;
    searchQueryDebounced.cancel();
    setInputValue(urlSearchTerm);
    setRequest((current) =>
      current.query === undefined ? current : { ...current, query: undefined },
    );
    setShowResults(false);
  }, [urlSearchTerm, searchQueryDebounced]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      searchQueryDebounced(event.target.value);
    },
    [searchQueryDebounced],
  );

  const submitTerm = inputValue.trim();

  const handleSearchSubmit = useCallback(() => {
    if (!submitTerm) return;
    setPendingSearchPath(buildProductSearchPath(submitTerm));
  }, [submitTerm]);

  // An external shi.com redirect can't be undone, so hold the submit until the flag settles.
  useEffect(() => {
    if (!pendingSearchPath || productListingLoading) return;
    setPendingSearchPath(undefined);
    if (productListingEnabled) {
      navigate(AppendIfPrefixExists(pendingSearchPath));
    } else {
      navigateToShiCom(pendingSearchPath);
    }
  }, [pendingSearchPath, productListingLoading, productListingEnabled, navigate]);

  const handleClose = useCallback(() => {
    setShowResults(false);
  }, []);

  const handleShowResults = useCallback(() => {
    if (!request.query?.length) return;
    if (products.length > 0) {
      setShowResults(true);
    }
  }, [request.query, products]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowDown" && showResults) {
        event.preventDefault();
        productListRef.current?.focusFirstItem();
      } else if (event.key === "Escape") {
        setShowResults(false);
      } else if (
        event.key === "Enter" &&
        !showResults &&
        submitTerm
      ) {
        event.preventDefault();
        handleSearchSubmit();
      }
    },
    [showResults, handleSearchSubmit, submitTerm],
  );

  return (
    <SearchBar
      value={inputValue}
      onSearchChange={handleSearchChange}
      onSearchSubmit={handleSearchSubmit}
      searchPlaceholder={searchPlaceholder}
      loading={enabled && isLoading}
      open={showResults}
      onClose={handleClose}
      onFocus={handleShowResults}
      onKeyDown={handleKeyDown}
    >
      <ProductList
        ref={productListRef}
        relatedProducts={products}
        onAddToCart={postProductAddShoppingCart}
      />
    </SearchBar>
  );
}
